<template>
  <dl class="mandate-summary mb-0">
    <dt>{{ $t("forwards.mandates.summary.companies") }}</dt>
    <dd data-testid="summary-companies">{{ memberNames.join(", ") }}</dd>

    <dt>{{ $t("forwards.mandates.summary.devices") }}</dt>
    <dd data-testid="summary-devices">
      {{
        form.device_scope === "allowlist"
          ? $t("forwards.mandates.summary.devices_allowlisted", devices.length)
          : $t("forwards.mandates.summary.devices_all", {
              count: devices.length,
            })
      }}
    </dd>

    <dt>{{ $t("forwards.mandates.summary.listing") }}</dt>
    <dd data-testid="summary-listing">
      <template v-if="form.listing_kind === 'pay_as_produced'">
        {{ $t("forwards.mandates.listing_kind.pay_as_produced") }}
      </template>
      <template v-else>
        <div>
          {{ $t(`forwards.mandates.listing_kind.${form.listing_kind}`) }} ·
          {{ $t(`forwards.mandates.entry_width.${width}`) }}
        </div>
        <p v-if="rows.length === 0" class="text-body-tertiary mb-0">
          {{ $t("forwards.mandates.volumes.no_rows") }}
        </p>
        <table v-else class="table table-sm mb-0 mandate-summary__rows">
          <tbody>
            <tr v-for="row in rows" :key="row.label">
              <td>{{ row.label }}</td>
              <td class="text-end text-nowrap">
                {{ $t("forwards.common.mwh_value", { value: row.volume }) }}
              </td>
            </tr>
          </tbody>
        </table>
        <MandateSalesScheduleButtonComponent
          :schedule="salesSchedule"
          class="mt-2"
        />
      </template>
      <p class="advisory-note mt-2 mb-0" data-testid="summary-obligation">
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{
          form.listing_kind === "pay_as_produced"
            ? $t("forwards.mandates.summary.obligation_note_pay_as_produced")
            : $t("forwards.mandates.summary.obligation_note")
        }}</span>
      </p>
    </dd>
  </dl>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type {
  MandateDeviceRow,
  SalesTimeline,
} from "@/forwards2/forwardsTypes";
import {
  periodLabelOfBounds,
  recurringSlotCount,
} from "@/forwards2/periodUtils";
import MandateSalesScheduleButtonComponent from "@/forwards2/mandates/MandateSalesScheduleButtonComponent.vue";
import { recurringSlotLabel } from "@/forwards2/mandates/mandateListingView";
import {
  selectedWidth,
  type MandateEditorForm,
} from "@/forwards2/mandates/mandateEditorForm";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The new-mandate wizard's read-only account of what the save will create: the companies, how many
 * devices sell, and the volume listed per period at the mandate's width. The listing is not binding,
 * but what a deal pulls from it is, and the creator is told so before the save.
 */
const props = defineProps<{
  form: MandateEditorForm;
  memberNames: string[];
  /** The mandate's own devices: every member device, or only the allowlisted ones. */
  devices: MandateDeviceRow[];
  /** The preview's sales schedule for the listing; null while there is no preview. */
  salesSchedule: SalesTimeline[] | null;
}>();

const { t } = useI18n();

const width = computed(() => selectedWidth(props.form));

const rows = computed(() => {
  if (props.form.listing_kind === "recurring") {
    const volumes = props.form.recurring[width.value].volumes;
    return Array.from(
      { length: recurringSlotCount(width.value) },
      (_unused, offset) => ({
        label: recurringSlotLabel(width.value, offset + 1, t),
        volume: formatNumberWithSpaces(volumes[String(offset + 1)] ?? 0),
      })
    );
  }
  return props.form.one_off[width.value].map((listing) => ({
    label:
      periodLabelOfBounds(listing.period_start, listing.period_end) ??
      listing.period_start,
    volume: formatNumberWithSpaces(listing.volume_mwh),
  }));
});
</script>

<style scoped lang="scss">
.mandate-summary {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.5rem 1.5rem;

  dt {
    font-weight: 600;
  }

  dd {
    margin: 0;
  }
}

.mandate-summary__rows {
  max-width: 20rem;
}
</style>
